import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SelectionPlugin } from '@atlaskit/editor-plugin-selection';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { convertProsemirrorNodeToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';

import type { EditorPluginAIProvider } from '../../types/types';

import { type SelectionPluginState, selectionPluginKey } from './selection-plugin-key';
import { isPseudoSelectionChanged } from './utils';

/**
 * Clear the selection context when the next user action interaction occurs
 */
const clearPendingSelectionContext = (view: EditorView) => {
	const isPending = selectionPluginKey.getState(view.state)?.isPendingSelectionContextClear;
	if (isPending) {
		const { dispatch, state } = view;
		dispatch(state.tr.setMeta(selectionPluginKey, { action: 'clearPendingSelectionContext' }));
	}
};

export const createSelectionPlugin = ({
	api,
	editorPluginAIProvider,
}: {
	api: PublicPluginAPI<SelectionPlugin> | undefined;
	editorPluginAIProvider: EditorPluginAIProvider;
}) => {
	let isMouseDown = false;
	let selectionChangedDuringDrag = false;

	return new SafePlugin<SelectionPluginState>({
		key: selectionPluginKey,
		state: {
			init: () => ({
				nonCursorSelectionChangeCount: 0,
				lastSelectionContext: undefined,
				isPendingSelectionContextClear: false,
			}),
			apply: (tr, pluginState) => {
				const meta = tr.getMeta(selectionPluginKey);
				if (meta?.action === 'incrementNonCursorSelectionChangeCount') {
					return {
						...pluginState,
						nonCursorSelectionChangeCount: pluginState.nonCursorSelectionChangeCount + 1,
					};
				} else if (meta?.action === 'lastSelectionContextPending') {
					return {
						...pluginState,
						isPendingSelectionContextClear: true,
					};
				} else if (meta?.action === 'lastSelectionContext') {
					const selection = meta.selection;
					return {
						...pluginState,
						isPendingSelectionContextClear: false,
						lastSelectionContext: selection
							? {
									selectionFragment:
										api?.selection?.actions?.getSelectionFragment({ selection }) ?? null,
									localIds: api?.selection?.actions?.getSelectionLocalIds({ selection }) ?? null,
									selectionMarkdown:
										convertProsemirrorNodeToMarkdown({
											node: selection.content().content,
										}).markdown ?? null,
								}
							: undefined,
					};
				} else if (
					pluginState?.isPendingSelectionContextClear &&
					meta?.action === 'clearPendingSelectionContext'
				) {
					return {
						...pluginState,
						lastSelectionContext: undefined,
						isPendingSelectionContextClear: false,
					};
				}
				return pluginState;
			},
		},
		appendTransaction: (_transactions, oldState, newState) => {
			const oldSel = oldState.selection;
			const newSel = newState.selection;

			const pseudoSelectionChanged = isPseudoSelectionChanged(oldState, newState);

			// Check for pseudo-selection changes first
			if (pseudoSelectionChanged) {
				// If pseudo-selection changed, always count as meaningful
				// unless we are dragging with mouse
				if (isMouseDown) {
					selectionChangedDuringDrag = true;
					return null;
				}
				return newState.tr.setMeta(selectionPluginKey, {
					action: 'incrementNonCursorSelectionChangeCount',
				});
			}

			// If no pseudo-selection change, check actual selection differences
			if (oldSel.eq(newSel)) {
				return null;
			}

			// If both are empty and no pseudo-change
			if (oldSel.empty && newSel.empty) {
				return null;
			}

			// If the mouse is currently down (e.g., during a drag), record that a change occurred
			// but do not increment the counter yet. The increment will happen when the mouse is released.
			if (isMouseDown) {
				selectionChangedDuringDrag = true;
				return null;
			}

			// If the mouse is not down, this change is considered final and should increment
			// the selection change count immediately.
			return newState.tr.setMeta(selectionPluginKey, {
				action: 'incrementNonCursorSelectionChangeCount',
			});
		},
		props: {
			handleDOMEvents: {
				paste: editorPluginAIProvider.isInDocumentStreamingEnabled
					? (view) => {
							clearPendingSelectionContext(view);
							return false;
						}
					: undefined,
				compositionstart: editorPluginAIProvider.isInDocumentStreamingEnabled
					? (view) => {
							clearPendingSelectionContext(view);
							return false;
						}
					: undefined,
				keydown: editorPluginAIProvider.isInDocumentStreamingEnabled
					? (view) => {
							clearPendingSelectionContext(view);
							return false;
						}
					: undefined,
				mousedown: (view) => {
					if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
						clearPendingSelectionContext(view);
					}

					// When the mouse goes down, we start "dragging" mode.
					isMouseDown = true;
					selectionChangedDuringDrag = false;
					return false; // Returning false to allow other handlers
				},
				mouseup: (view) => {
					// When the mouse is released, if a selection change happened during dragging,
					// we increment the counter once.
					isMouseDown = false;
					if (selectionChangedDuringDrag) {
						const { state, dispatch } = view;
						dispatch(
							state.tr.setMeta(selectionPluginKey, {
								action: 'incrementNonCursorSelectionChangeCount',
							}),
						);
						selectionChangedDuringDrag = false;
					}
					return false; // Returning false to allow other handlers
				},
			},
		},
	});
};
